/**
 * # Apollo Client Field Policy for 'Load More' Style Pagination
 *
 * ## Why This Custom Field Policy is Necessary:
 *
 * Apollo Client's default caching mechanism normalizes data, storing individual entities
 * (like a 'Rental' or 'User') separately. When a paginated query fetches a list, the
 * items in that list (e.g., `response.nodes`) are often stored in the cache as
 * references (e.g., `{ __ref: "Rental:some-uuid" }`) to these normalized entities,
 * rather than the full objects.
 *
 * When using `fetchMore` for offset-based pagination, Apollo Client needs explicit
 * instructions on how to:
 * 1.  **Identify the Correct Cached List: ** Subsequent `fetchMore` calls must merge
 *     results into the *same* cached list as the initial query. This is controlled
 *     by `keyArgs`, which should include query arguments defining the list's identity
 *     (e.g., filters, search terms) but *exclude* pagination arguments like `offset`
 *     and `limit`.
 * 2.  **Merge Incoming Data with Existing Data: ** Since the cached list items (`nodes`)
 *     are references, simply concatenating arrays of these references is not enough
 *     if de-duplication is needed or if the server might return overlapping data.
 *     We must:
 *     a.  **Read Actual IDs: ** Use Apollo's `readField` utility to retrieve the actual
 *         `id` field from each reference object. Direct access like `node.id` on a
 *         reference object will yield `undefined`.
 *     b.  **De-duplicate: ** Ensure that items fetched in the following pages are not
 *         re-added if their IDs are already present in the existing cached list.
 *     c.  **Append Correctly: ** Place the new, unique items at the correct position
 *         in the list, typically by appending them for "load more" functionality
 *         where `offset` indicates the start of the new data.
 *
 * This `paginatedField` helper provides a reusable `FieldPolicy` that encapsulates
 * this logic, ensuring that offset-based pagination with `fetchMore` works correctly
 * with Apollo Client's normalized cache. It handles reading IDs from references,
 * de-duplicating new items, and merging the lists of references.
 */
import type {FieldPolicy, StoreObject, Reference, FieldFunctionOptions} from '@apollo/client'

type NodeType<S_Node_Full> = S_Node_Full | Reference
type ReadFieldFn = <V = any>(fieldName: string, from?: StoreObject | Reference) => V | undefined
type PaginatedResponse<S_Node_Full> = {
  nodes: Array<NodeType<S_Node_Full>>
  totalCount: number
  __typename?: string
}
type NodeWithId = StoreObject & {id: string; __typename?: string}

export const paginatedField = <T_Response_Concrete extends PaginatedResponse<S_Node_Concrete>, S_Node_Concrete extends NodeWithId>(
  identifyingKeyArgs: string[] | false,
): FieldPolicy<T_Response_Concrete> => {
  const mergeFunction = (
    existing: Readonly<T_Response_Concrete> | undefined,
    incoming: Readonly<T_Response_Concrete>,
    options: FieldFunctionOptions,
  ): Readonly<T_Response_Concrete> | undefined => {
    const args = options.args as {pagination?: {offset?: number; limit?: number}} | null | undefined
    const readField = options.readField as ReadFieldFn
    const offset = args?.pagination?.offset ?? 0

    if (!existing || offset === 0) {
      return incoming
    }

    const existingNodeRefs = existing.nodes || []
    const incomingNodeRefs = incoming.nodes || []

    const uniqueNewIncomingNodeRefs = getUniqueNewNodeReferences<S_Node_Concrete>(existingNodeRefs, incomingNodeRefs, readField)

    let finalMergedNodeRefs: Array<NodeType<S_Node_Concrete>>

    if (uniqueNewIncomingNodeRefs.length > 0) {
      finalMergedNodeRefs = mergeNodeReferences<S_Node_Concrete>(existingNodeRefs, uniqueNewIncomingNodeRefs, offset)
    } else {
      if (!didMetadataChange(existing, incoming)) {
        return existing
      }
      finalMergedNodeRefs = existingNodeRefs.slice(0)
    }

    return buildResultResponse<S_Node_Concrete, T_Response_Concrete>(incoming, finalMergedNodeRefs)
  }

  return {
    keyArgs: identifyingKeyArgs,
    merge: mergeFunction as any,
  }
}

// Below: Helper Functions

function getUniqueNewNodeReferences<S_Node_Full extends NodeWithId>(
  existingNodeRefs: ReadonlyArray<NodeType<S_Node_Full>>,
  incomingNodeRefs: ReadonlyArray<NodeType<S_Node_Full>>,
  readField: ReadFieldFn,
): Array<NodeType<S_Node_Full>> {
  const existingNodeIdsSet = new Set<string | undefined>(existingNodeRefs.map((nodeRef) => readField<string>('id', nodeRef)))
  return incomingNodeRefs.filter((newNodeRef) => !existingNodeIdsSet.has(readField<string>('id', newNodeRef)))
}

function mergeNodeReferences<S_Node_Full extends NodeWithId>(
  existingNodeRefs: ReadonlyArray<NodeType<S_Node_Full>>,
  uniqueNewIncomingNodeRefs: ReadonlyArray<NodeType<S_Node_Full>>,
  offset: number,
): Array<NodeType<S_Node_Full>> {
  if (offset === existingNodeRefs.length) {
    return [...existingNodeRefs, ...uniqueNewIncomingNodeRefs]
  } else {
    const tempMerged = existingNodeRefs.slice(0)
    for (let i = 0; i < uniqueNewIncomingNodeRefs.length; ++i) {
      tempMerged[offset + i] = uniqueNewIncomingNodeRefs[i]
    }
    return tempMerged.filter((ref) => ref !== undefined)
  }
}

function didMetadataChange<T_Response extends PaginatedResponse<any>>(existing: Readonly<T_Response>, incoming: Readonly<T_Response>): boolean {
  return existing.totalCount !== incoming.totalCount || existing.__typename !== incoming.__typename
}

function buildResultResponse<S_Node_Full extends NodeWithId, T_Response extends PaginatedResponse<S_Node_Full>>(
  incomingResponseBasis: Readonly<T_Response>,
  mergedNodeRefs: Array<NodeType<S_Node_Full>>,
): T_Response {
  return {
    __typename: incomingResponseBasis.__typename,
    totalCount: incomingResponseBasis.totalCount,
    nodes: mergedNodeRefs,
  } as T_Response
}
